import { Component, OnDestroy, OnInit } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject } from 'rxjs';
import { ProductOrientation } from 'src/app/models/layout/product-orientation';
import { ImageService } from 'src/app/services/image.service';
import { FileDownloadService } from 'src/app/services/file-download.service';
import { environment } from 'src/environments/environment';
import { ImportImageResponse } from 'src/app/models/image';
import { FormsModule } from '@angular/forms';
import { NgFor, KeyValuePipe } from '@angular/common';

interface ImageUpload {
  file: File;
  barcode: string;
  orientation: ProductOrientation;
  preview?: SafeUrl;
  error?: string;
  uploaded?: boolean;
  message?: string;
}

@Component({
    selector: 'app-images-manager',
    templateUrl: './images-manager.component.html',
    styleUrl: './images-manager.component.scss',
    imports: [
        FormsModule,
        NgFor,
        KeyValuePipe,
    ],
})
export class ImagesManagerComponent implements OnDestroy {
  private apiUrl = environment.apiUrl;
  uploads: ImageUpload[] = [];
  orientations = ProductOrientation;
  processing = false;
  skipBackgroundRemoval = false;
  replaceIfExists = true;
  downloadBarcode: string = '';
  downloadOrientation: ProductOrientation = ProductOrientation.FRONT;

  constructor(
    public activeModal: NgbActiveModal,
    private imageService: ImageService,
    private fileDownloadService: FileDownloadService,
  ) {}

  ngOnDestroy(): void {
    // Clean up any previews
    this.uploads.forEach((upload) => {
      if (upload.preview) {
        URL.revokeObjectURL(upload.preview as string);
      }
    });
  }

  get totalFiles(): number {
    return this.uploads.length;
  }

  get uploadedCount(): number {
    return this.uploads.filter((u) => u.uploaded && !u.error).length;
  }

  get failedCount(): number {
    return this.uploads.filter((u) => u.error).length;
  }

  get pendingCount(): number {
    return this.uploads.filter((u) => !u.uploaded && !u.error).length;
  }

  handleBulkUpload(event: any) {
    const files = event.target.files;
    if (files) {
      Array.from(files).forEach((file) => {
        // Expected format: {barcode}.{orientation}.{extension}
        // The barcode may carry an optional tray (T) / case (C) suffix, e.g. 568436875432T.1.png
        const matches = (file as File).name.match(
          /^(\d+[TC]?)\.(\d)\.(jpg|png|webp)$/i
        );
        if (matches) {
          const [_, rawBarcode, orientationNum] = matches;
          // Normalise the optional tray/case marker to uppercase to match the request/storage key.
          const barcode = rawBarcode.toUpperCase();
          const orientation = parseInt(orientationNum) as ProductOrientation;

          if (this.isValidOrientation(orientation)) {
            this.addUpload({
              file: file as File,
              barcode,
              orientation,
            });
          }
        }
      });
    }
  }

  private addUpload(upload: ImageUpload) {
    // Create preview
    upload.preview = URL.createObjectURL(upload.file) as SafeUrl;
    this.uploads.push(upload);
  }

  private isValidOrientation(orientation: number): boolean {
    return [
      ProductOrientation.FRONT,
      ProductOrientation.SIDE,
      ProductOrientation.FLATPACK,
    ].includes(orientation);
  }

  removeUpload(index: number) {
    const upload = this.uploads[index];
    if (upload.preview) {
      URL.revokeObjectURL(upload.preview as string);
    }
    this.uploads.splice(index, 1);
  }

  async processUploads() {
  this.processing = true;

  const pendingUploads = this.uploads.filter(
    (upload) => !upload.uploaded || upload.error
  );

  const batchSize = 16;
  for (let i = 0; i < pendingUploads.length; i += batchSize) {
    const batch = pendingUploads.slice(i, i + batchSize);

    // Process 8 uploads in parallel
    const uploadPromises = batch.map(async (upload) => {
      try {
        const formData = new FormData();
        formData.append('file', upload.file);
        formData.append('barcode', upload.barcode);
        formData.append('orientation', upload.orientation.toString());
        formData.append('prepareImage', (!this.skipBackgroundRemoval).toString());
        formData.append('replaceIfExists', this.replaceIfExists.toString());

        const response = await fetch(`${this.apiUrl}/images/import-image`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorText = await response.text();
          upload.error = errorText || 'Failed to upload image';
          upload.uploaded = false;
        } else {
          const result = (await response.json()) as ImportImageResponse;
          if (result.success) {
            upload.error = undefined;
            upload.uploaded = true;
            upload.message = result.message;
            this.imageService.invalidateCacheEntry(
              upload.barcode,
              upload.orientation
            );
          } else {
            upload.error = result.message || 'Upload failed';
            upload.uploaded = false;
          }
        }
      } catch (error) {
        upload.error = 'Error uploading image';
        upload.uploaded = false;
        console.error('Upload error:', error);
      }
    });

    await Promise.all(uploadPromises);
  }

  this.processing = false;
}

  async downloadImage() {
    if (!this.downloadBarcode || !this.downloadOrientation) return;

    try {
      const response = await fetch(
        `${this.apiUrl}/images/download-image/${this.downloadBarcode}/${this.downloadOrientation}`
      );

      if (!response.ok) {
        alert('Image not found or failed to download.');
        return;
      }

      const blob = await response.blob();
      this.fileDownloadService.triggerDownload(
        blob,
        `${this.downloadBarcode}.${this.downloadOrientation}.jpg`
      );
    } catch (err) {
      console.error('Download failed:', err);
      alert('Error downloading image.');
    }
  }

  cancel(): void {
    this.activeModal.close(null);
  }
}